import { Controller, useForm } from "react-hook-form";
import { StyledFolderIcon } from "../../StyledComponents";
import {
    ListMediaFoldersResponseParams,
    UpdateMediaFoldersRequestParams,
    UpdateMediaFoldersResponseParams,
    UNFILED_FOLDER_ID,
} from "../../types";
import { useEffect, useMemo, useRef } from "react";
import { useFoldersStore } from "../../foldersStore";
import { FormLabel } from "@kaltura/mediaspace-shared-ui/form";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { TreeView } from "@kaltura/ds-react-components";
import { ToastMessages } from "@kaltura/mediaspace-shared-ui";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";

export interface UpdateMediaFoldersFormProps {
    /** callback to fetch current entry folders */
    fetchData: () => ListMediaFoldersResponseParams;
    /** entry ids to update folders for */
    entryIds: string[];
    /**
     * currently selected folder id in the search filters
     * used to decide if media list should be refreshed after submit
     */
    currentFolderId: string;
    /** callback for letting the app know media list should be refreshed */
    reloadMedia?: () => void;
    /** allow menu items to close the menu */
    onCloseMenu?: () => void;
    /** callback to call when form isDirty changes */
    onIsDirty: (value: boolean) => void;
    /** callback to call when update media folders request is processing */
    onIsProcessing: (value: boolean) => void;
}

const baseClassName = "kms-ds-update-media-folders-form";

export function UpdateMediaFoldersForm({
    fetchData,
    entryIds,
    reloadMedia,
    currentFolderId,
    onCloseMenu,
    onIsDirty,
    onIsProcessing,
}: UpdateMediaFoldersFormProps) {
    /**
     * current folders the entry is associated with
     * relevant only for single entry flow
     */
    const { entryFolders } = fetchData();
    const {
        handleSubmit,
        control,
        formState: { isDirty },
    } = useForm<{ selectedIds: string[] }>({
        mode: "onChange",
        defaultValues: {
            // sort for isDirty to work
            // react-hook-form compares array order and values
            selectedIds: [...(entryFolders ?? [])].sort(),
        },
    });
    const shouldReloadMedia = useRef(false);
    const foldersTreeItems = useFoldersStore((state) => state.foldersTreeItems);
    const foldersMap = useFoldersStore((state) => state.foldersMap);

    const { showToast } = useToastsContext();

    /**
     * reload my media list after update media folders request was submitted
     * done here instead of in below onSubmit to allow media actions menu
     * and media folders modal to close and only then reload the media list
     */
    useEffect(() => {
        return () => {
            if (shouldReloadMedia.current) {
                reloadMedia?.();
            }
        };
    }, []);

    /**
     * update parent isDirty by form state to disable the submit button
     */
    useEffect(() => {
        onIsDirty(isDirty);
    }, [isDirty]);

    const onSubmit = async ({ selectedIds }: { selectedIds: string[] }) => {
        onIsProcessing(true);
        const addFolderIds = entryFolders ? selectedIds.filter((id) => !entryFolders.includes(id)) : selectedIds;
        const removeFolderIds = entryFolders ? entryFolders.filter((id) => !selectedIds.includes(id)) : [];
        const requestData: UpdateMediaFoldersRequestParams = {
            entryIds: entryIds,
            addFolderIds: addFolderIds,
            removeFolderIds: removeFolderIds,
        };
        try {
            const result: UpdateMediaFoldersResponseParams = await postKmsData(
                `${baseUrl}/folders/entry/update`,
                requestData
            );
            onIsProcessing(false);

            if (result.successMessages && result.successMessages.length > 0) {
                // multiple success messages allowed in single entry flow (add to folder, remove from folder)
                showToast({
                    severity: "success",
                    message: (
                        <ToastMessages
                            title={translate("Media successfully updated in folders:")}
                            messages={result.successMessages}
                        />
                    ),
                });
                // reload media list if:
                // currently filtered folder is unfiled folder or
                // entries were removed from the currently filtered folder
                // see above useEffect for more details
                if (currentFolderId === UNFILED_FOLDER_ID || removeFolderIds.includes(currentFolderId)) {
                    shouldReloadMedia.current = true;
                }
                onCloseMenu?.();
            }
            // the server returns either successMessages or errorMessages - not both
            else {
                if (result.errorMessages && result.errorMessages.length > 0) {
                    showToast({
                        severity: "error",
                        message: (
                            <ToastMessages
                                title={translate("Failed to complete the following updates:")}
                                messages={result.errorMessages}
                            />
                        ),
                    });
                }
            }
        }
        catch (e) {
            onIsProcessing(false);
            showToast({
                severity: "error",
                message: translate("Failed to update media folders"),
            });
        }
    };

    const defaultExpanded = useMemo(() => {
        if (!entryFolders) return [];

        const expandedSet = new Set<string>();

        for (const folderId of entryFolders) {
            const folder = foldersMap.get(folderId);
            if (!folder) continue;

            // Add fullPathIds except the last one (item itself)
            const parentPathIds = folder.fullPathIds.slice(0, -1);
            parentPathIds.forEach((id) => expandedSet.add(id));
        }

        return Array.from(expandedSet);
    }, [entryFolders, foldersMap]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} id={"update-media-folders"} className={`${baseClassName}-form`}>
            <FormLabel htmlFor={"entriesFolders"} fontWeightBold={true} text={translate("Choose location")} />
            <Controller
                name={"selectedIds"}
                control={control}
                render={({ field }) => (
                    <TreeView
                        id={"entriesFolders"}
                        items={foldersTreeItems}
                        defaultStartIcon={<StyledFolderIcon />}
                        iconExpansionOnly={true}
                        multiSelect={true}
                        selectionComponentType={"checkbox"}
                        defaultSelected={entryFolders ?? []}
                        defaultExpanded={defaultExpanded}
                        onNodeSelect={(event, nodeIds) => {
                            // sort for isDirty to work
                            // react-hook-form compares array order and values
                            field.onChange([...nodeIds].sort());
                        }}
                    />
                )}
            />
        </form>
    );
}
